import { gql } from '@/__generated__';
import type { DevicesAnalysis_GetDevicesQuery } from '@/__generated__/graphql';

export type Device = DevicesAnalysis_GetDevicesQuery['devices'][number];
export type Gate = DevicesAnalysis_GetDevicesQuery['gates'][number];

const DEVICES_ANALYSIS_GET_DEVICES = gql(`#graphql
  query DevicesAnalysis_GetDevices(
    $input: DevicesInput,
    $gatesInput: GatesInput,
    $metricsInput: MetricsParametersInput,
    $eventsInput: MetricsParametersInput,
    $fluxRange: InfluxRangeInput,
    $flowUnit: MetricUnit,
    $levelUnit: MetricUnit,
    $volumeHourUnit: MetricUnit,
    $volumeDayUnit: MetricUnit,
    $volumeMonthUnit: MetricUnit,
    $accumulatedVolumeUnit: MetricUnit,
    $storedVolumeUnit: MetricUnit,
    $connectionStateInput: MetricsParametersInput,
    $includeFlow: Boolean!,
    $includeFlowRaw: Boolean!,
    $includeLevel: Boolean!,
    $includeLevelRaw: Boolean!,
    $includeVelocity: Boolean!,
    $includeVelocityRaw: Boolean!,
    $includeDopplerVelocity: Boolean!,
    $includeDopplerVelocityRaw: Boolean!,
    $includeAngle: Boolean!,
    $includeAngleNonCalibrated: Boolean!,
    $includeCalculatedAngle: Boolean!,
    $includePosition: Boolean!,
    $includeUpstreamLevel: Boolean!,
    $includeDownstreamLevel: Boolean!,
    $includeOriginalDistance: Boolean!,
    $includeSignalQuality: Boolean!,
    $includeConnectionState: Boolean!,
    $includeAccumulatedVolume: Boolean!,
    $includeVolumeHour: Boolean!,
    $includeVolumeDay: Boolean!,
    $includeVolumeMonth: Boolean!,
    $includeBatterySoc: Boolean!,
    $includeBatteryV: Boolean!,
    $includeChargeI: Boolean!,
    $includeChargeP: Boolean!,
    $includeSolarI: Boolean!,
    $includeSolarP: Boolean!,
    $includeSolarV: Boolean!,
    $includeFlowAlerts: Boolean!,
    $includeLevelAlerts: Boolean!,
    $includeDistributionAlerts: Boolean!,
    $includeRatingCurvesLimits: Boolean!,
    $includeEvents: Boolean!,
    # $includeDgaFlow: Boolean!,
    # $includeDgaLevel: Boolean!,
    # $includeDgaAccumulatedVol: Boolean!,
    # $dgaLevelUnit: LevelUnit,
    $includeSnowLevel: Boolean!,
    $includeSnowLevelRaw: Boolean!,
    $includeSnowLevelNonCalibrated: Boolean!,
    $includeSnowTemperature: Boolean!,
    $includeSnowTemperatureRaw: Boolean!,
    $includeSnowRadiation: Boolean!,
    $includeSnowRadiationRaw: Boolean!,
    $includeSnowWaterEquivalent: Boolean!,
    $includeSnowWaterEquivalentRaw: Boolean!,
    $includeSnowWeight: Boolean!,
    $includeSweNonCalibrated: Boolean!,
    $includeTemperature: Boolean!,
    $includeTemperatureRaw: Boolean!,
    $includeConductivity: Boolean!,
    $includeConductivityRaw: Boolean!,
    $includePh: Boolean!,
    $includePhRaw: Boolean!,
    $includeOrp: Boolean!,
    $includeOrpRaw: Boolean!,
    $includeDissolvedOxygen: Boolean!,
    $includeDissolvedOxygenRaw: Boolean!,
    $includeDissolvedOxygenSaturation: Boolean!,
    $includeDissolvedOxygenSaturationRaw: Boolean!,
    $includeTurbidityNtu: Boolean!,
    $includeTurbidityNtuRaw: Boolean!,
    $includeTurbidityFnu: Boolean!,
    $includeTurbidityFnuRaw: Boolean!,
    $includeAllocation: Boolean!,
    $includeStoredVolume: Boolean!,
  ) {
    devices(input: $input) {
      id
      profile {
        id
        name
        type
        typeVersion
        sensor
        source
        sourceInfo
        operationalState
        operationalStates(fluxRange: $fluxRange) {
          timestamp
          value
        }
        logicalDeviceID
        icnCollection {
          id
          name
        }
        organization {
          id
          name
        }
        location {
          latitude
          longitude
        }
      }
      alerts {
        id
        basicConfig: basicConfigurations {
          id
          flow(unit: $flowUnit) @include(if: $includeFlowAlerts) {
            timestamp
            min
            max
            overflow
            precision
            unit
          }
          level(unit: $levelUnit) @include(if: $includeLevelAlerts) {
            timestamp
            min
            max
            overflow
            precision
            unit
          }
          lastFlow(unit: $flowUnit) @include(if: $includeFlowAlerts) {
            timestamp
            min
            max
            overflow
            precision
            unit
          }
          lastLevel(unit: $levelUnit) @include(if: $includeLevelAlerts) {
            timestamp
            min
            max
            overflow
            precision
            unit
          }
        }
      }
      distributionLimits {
        maxFlow @include(if: $includeDistributionAlerts)
        precision
        unit
      }
      metrics(input: $metricsInput) {
        id
        availableFields
        flow(unit: $flowUnit) {
          data @include(if: $includeFlow)
          lastData
          unit
          precision
          metricField
        }
        flowRaw(unit: $flowUnit) {
          data @include(if: $includeFlowRaw)
          unit
          precision
          metricField
        }
        allocation {
          data @include(if: $includeAllocation)
          unit
          precision
          metricField
        }
        level(unit: $levelUnit) {
          data @include(if: $includeLevel)
          lastData
          unit
          precision
          metricField
        }
        levelRaw(unit: $levelUnit) {
          data @include(if: $includeLevelRaw)
          unit
          precision
          metricField
        }
        position {
          data @include(if: $includePosition)
          lastData
          unit
          precision
          metricField
        }
        angle {
          data @include(if: $includeAngle)
          unit
          precision
          metricField
        }
        angleNonCalibrated {
          data @include(if: $includeAngleNonCalibrated)
          unit
          precision
          metricField
        }
        calculatedAngle {
          data @include(if: $includeCalculatedAngle)
          unit
          precision
          metricField
        }
        velocity {
          data @include(if: $includeVelocity)
          unit
          precision
          metricField
        }
        velocityRaw {
          data @include(if: $includeVelocityRaw)
          unit
          precision
          metricField
        }
        dopplerVelocity {
          data @include(if: $includeDopplerVelocity)
          unit
          precision
          metricField
        }
        dopplerVelocityRaw {
          data @include(if: $includeDopplerVelocityRaw)
          unit
          precision
          metricField
        }
        originalDistance(unit: $levelUnit) {
          data @include(if: $includeOriginalDistance)
          unit
          precision
          metricField
        }
        signalQuality {
          data @include(if: $includeSignalQuality)
          unit
          precision
          metricField
        }
        connectionState(input: $connectionStateInput) {
          data @include(if: $includeConnectionState)
          unit
          precision
          metricField
        }
        batterySoc {
          data @include(if: $includeBatterySoc)
          unit
          precision
          metricField
        }
        batteryV {
          data @include(if: $includeBatteryV)
          unit
          precision
          metricField
        }
        chargeI {
          data @include(if: $includeChargeI)
          unit
          precision
          metricField
        }
        chargeP {
          data @include(if: $includeChargeP)
          unit
          precision
          metricField
        }
        solarI {
          data @include(if: $includeSolarI)
          unit
          precision
          metricField
        }
        solarP {
          data @include(if: $includeSolarP)
          unit
          precision
          metricField
        }
        solarV {
          data @include(if: $includeSolarV)
          unit
          precision
          metricField
        }
        temperature {
          data @include(if: $includeTemperature)
          unit
          precision
          metricField
        }
        temperatureRaw {
          data @include(if: $includeTemperatureRaw)
          unit
          precision
          metricField
        }
        conductivity {
          data @include(if: $includeConductivity)
          unit
          precision
          metricField
        }
        conductivityRaw {
          data @include(if: $includeConductivityRaw)
          unit
          precision
          metricField
        }
        ph {
          data @include(if: $includePh)
          unit
          precision
          metricField
        }
        phRaw {
          data @include(if: $includePhRaw)
          unit
          precision
          metricField
        }
        oxidationReductionPotential {
          data @include(if: $includeOrp)
          unit
          precision
          metricField
        }
        oxidationReductionPotentialRaw {
          data @include(if: $includeOrpRaw)
          unit
          precision
          metricField
        }
        dissolvedOxygen {
          data @include(if: $includeDissolvedOxygen)
          unit
          precision
          metricField
        }
        dissolvedOxygenRaw {
          data @include(if: $includeDissolvedOxygenRaw)
          unit
          precision
          metricField
        }
        dissolvedOxygenSaturation {
          data @include(if: $includeDissolvedOxygenSaturation)
          unit
          precision
          metricField
        }
        dissolvedOxygenSaturationRaw {
          data @include(if: $includeDissolvedOxygenSaturationRaw)
          unit
          precision
          metricField
        }
        turbidityNTU {
          data @include(if: $includeTurbidityNtu)
          unit
          precision
          metricField
        }
        turbidityNTURaw {
          data @include(if: $includeTurbidityNtuRaw)
          unit
          precision
          metricField
        }
        turbidityFNU {
          data @include(if: $includeTurbidityFnu)
          unit
          precision
          metricField
        }
        turbidityFNURaw {
          data @include(if: $includeTurbidityFnuRaw)
          unit
          precision
          metricField
        }
        snowLevel {
          data @include(if: $includeSnowLevel)
          lastData
          unit
          precision
          metricField
        }
        snowLevelRaw {
          data @include(if: $includeSnowLevelRaw)
          unit
          precision
          metricField
        }
        snowLevelNonCalibrated {
          data @include(if: $includeSnowLevelNonCalibrated)
          unit
          precision
          metricField
        }
        snowTemperature {
          data @include(if: $includeSnowTemperature)
          unit
          precision
          metricField
        }
        snowTemperatureRaw {
          data @include(if: $includeSnowTemperatureRaw)
          unit
          precision
          metricField
        }
        snowRadiation {
          data @include(if: $includeSnowRadiation)
          unit
          precision
          metricField
        }
        snowRadiationRaw {
          data @include(if: $includeSnowRadiationRaw)
          unit
          precision
          metricField
        }
        snowWaterEquivalent {
          data @include(if: $includeSnowWaterEquivalent)
          unit
          precision
          metricField
        }
        snowWaterEquivalentRaw {
          data @include(if: $includeSnowWaterEquivalentRaw)
          unit
          precision
          metricField
        }
        snowWaterEquivalentNonCalibrated {
          data @include(if: $includeSweNonCalibrated)
          unit
          precision
          metricField
        }
        snowWeight {
          data @include(if: $includeSnowWeight)
          unit
          precision
          metricField
        }
        volumeHour: calculatedVolume(unit: $volumeHourUnit, input: { window: "1h" }) {
          data @include(if: $includeVolumeHour)
          unit
          precision
          metricField
        }
        volumeDay: calculatedVolume(unit: $volumeDayUnit, input: {
          aggregateWindow: {
            every: "1d",
            fn: SUM,
            removeIncompleteFirstWindow: true,
          }
        }) {
          data @include(if: $includeVolumeDay)
          unit
          precision
          metricField
        }
        volumeMonth: calculatedVolume(unit: $volumeMonthUnit, input: {
          aggregateWindow: {
            every: "1mo",
            fn: SUM,
            removeIncompleteFirstWindow: true,
          }
        }) {
          data @include(if: $includeVolumeMonth)
          unit
          precision
          metricField
        }
        accumulatedVolume(unit: $accumulatedVolumeUnit) {
          data @include(if: $includeAccumulatedVolume)
          unit
          precision
          metricField
        }
        storedVolume(unit: $storedVolumeUnit) {
          data @include(if: $includeStoredVolume)
          lastData
          unit
          precision
          metricField
        }
        events(input: $eventsInput) {
          data @include(if: $includeEvents) {
            timestamp
            value
            source
            userId
          }
        }
      }
      dataConfiguration @include(if: $includeRatingCurvesLimits) {
        deviceId
        ratingCurves {
          limits {
            min
            max
          }
        }
      }
    }
    gates: gates2(input: $gatesInput) {
      id
      downstreamDevice {
        id
        metrics(input: $metricsInput) {
          level(unit: $levelUnit) {
            data @include(if: $includeDownstreamLevel)
            unit
            precision
            metricField
          }
        }
      }
      upstreamDevice {
        id
        metrics(input: $metricsInput) {
          level(unit: $levelUnit) {
            data @include(if: $includeUpstreamLevel)
            unit
            precision
            metricField
          }
        }
      }
      hasAlarm
      alarmData
    }
  }
`);

export default DEVICES_ANALYSIS_GET_DEVICES;
